import Vue from 'vue'
import Router from 'vue-router'
import Meta from 'vue-meta'
import { i18n, loadLanguageAsync } from '@/i18n' // import VueI18n instance
import store from '@/store'
import readRoutes from '@/router/productions/read'
import listenRoutes from '@/router/productions/listen'
import concertRoutes from '@/router/productions/concert'
import cssRoutes from '@/router/productions/css'
import airRoutes from '@/router/productions/air'
import { checkProduction } from '@/assets/constants/production'
import { getLocalStorage } from '@/assets/helpers/UtilHelper'

// Use lazy loading
const Layout = () => import(/* webpackChunkName: "Layout" */ '@/layouts/Layout')
const Read = () => import(/* webpackChunkName: "Read" */ '@/pages/_locale/Read')
const Listen = () => import(/* webpackChunkName: "Listen" */ '@/pages/_locale/Listen')
const Concert = () => import(/* webpackChunkName: "Concert" */ '@/pages/_locale/Concert')
const CSS = () => import(/* webpackChunkName: "ClassicalSuperSearch" */ `@/pages/_locale/ClassicalSuperSearch`)
const AIR = () => import(/* webpackChunkName: "AIR" */ `@/pages/_locale/AIR`)
const Terms = () => import(/* webpackChunkName: "Terms" */ '@/pages/_locale/Terms')
const Privacy = () => import(/* webpackChunkName: "Privacy" */ '@/pages/_locale/Privacy')
const About = () => import(/* webpackChunkName: "About" */ '@/pages/_locale/About')
const Cooperation = () => import(/* webpackChunkName: "Cooperation" */ '@/pages/_locale/Cooperation')
const MediaKit = () => import(/* webpackChunkName: "MediaKit" */ '@/pages/_locale/MediaKit')
// const Membership = () => import(/* webpackChunkName: "Membership" */ '@/pages/_locale/Membership')
const Member = () => import(/* webpackChunkName: "Member" */ '@/pages/_locale/member/_id')
const Notification = () => import(/* webpackChunkName: "Notification" */ '@/pages/_locale/member/notification/_id')
const Transaction = () => import(/* webpackChunkName: "Membership" */ '@/pages/_locale/Transaction')

const Auth = () => import(/* webpackChunkName: "Notification" */ '@/layouts/Auth')
const SignIn = () => import(/* webpackChunkName: "SignIn" */ '@/pages/_locale/auth/SignIn')
const Reset = () => import(/* webpackChunkName: "Reset" */ '@/pages/_locale/auth/Reset')

const Search = () => import(/* webpackChunkName: "Search" */ '@/pages/_locale/search/Search')

const GameLayout = () => import(/* webpackChunkName: "Game" */ '@/layouts/Game')
const Game = () => import(/* webpackChunkName: "Game" */ '@/pages/_locale/Game')
const Piano = () => import(/* webpackChunkName: "Game" */ '@/pages/_locale/game/index')

// /////////////
// For dynamic setting index page
const productions = {
  READ: Read,
  LISTEN: Listen,
  CONCERT: Concert,
  CSS,
  AIR
}

Vue.use(Router)
Vue.use(Meta)
const currentProduction = checkProduction()
const productionName = currentProduction.toUpperCase()
const productionPath = currentProduction.toLowerCase()
const productionSubdomainKey = 'VUE_APP_MUZIK_' + currentProduction.toUpperCase()
const productionDomainKey = 'VUE_APP_MUZIK_DOMAIN'
const indexMetaImage = `https://${process.env[productionSubdomainKey]}.${process.env[productionDomainKey]}/img/cover-social/${productionPath}.png`
const baseRotes = [
  // /////////////////////////
  // Migration from 聽古典、閱古典
  // /////////////////////////
  {
    path: '/:locale/policy', // policy
    redirect: '/:locale/terms'
  },
  {
    path: '/:locale/partner', // policy
    redirect: '/:locale/about'
  },
  {
    path: '/:locale/media-kit', // policy
    redirect: '/:locale/mediaKit'
  },
  {
    path: '/:locale/search/:keyword', // search
    redirect: to => {
      const { params } = to
      const formattedPath = `${params.locale}/search`
      return { path: formattedPath, query: { q: params.keyword } }
    }
  },
  {
    path: '/:locale/search/:keyword/page/:paging', // search
    redirect: to => {
      const { params } = to
      const formattedPath = `${params.locale}/search`
      return { path: formattedPath, query: { q: params.keyword } }
    }
  },
  // ////////////////////////
  {
    path: '/',
    redirect: { name: 'index', params: { locale: i18n.navigatorLocalePath() } }
  },
  {
    path: '*',
    redirect: '/'
  },
  {
    path: '/:locale',
    component: Layout,
    children: [
      {
        path: '',
        name: 'index',
        component: productions[productionName], // Dynamic setting index and meta data
        meta: {
          requiresAuth: false,
          breadcrumbs: [],
          metaInfo: {
            title: `${productionName}.TITLE`,
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  },
  {
    path: '/:locale/auth',
    component: Auth,
    children: [
      {
        path: '',
        redirect: { name: 'signin' }
      },
      {
        path: 'signin',
        name: 'signin',
        component: SignIn,
        meta: {
          requiresAuth: false,
          breadcrumbs: [],
          metaInfo: {
            title: 'AUTH.SIGN_IN',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      },
      {
        path: 'reset/:token',
        name: 'reset',
        component: Reset,
        meta: {
          requiresAuth: false,
          breadcrumbs: [],
          metaInfo: {
            title: 'AUTH.RESET',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  },
  {
    path: '/:locale/search',
    component: Layout,
    children: [
      {
        path: '',
        name: 'search',
        component: Search,
        meta: {
          requiresAuth: false,
          breadcrumbs: [],
          metaInfo: {
            title: 'SEARCH.TITLE',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  },
  {
    path: '/:locale/terms',
    component: Layout,
    children: [
      {
        path: '',
        name: 'terms',
        component: Terms,
        meta: {
          requiresAuth: false,
          breadcrumbs: [
            { routeName: 'index', localeLabel: 'BREADCRUMBS.INDEX', redirectPath: '' },
            { routeName: '', localeLabel: 'TERMS.TITLE', redirectPath: '' }
          ],
          metaInfo: {
            title: 'TERMS.TITLE',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  },
  {
    path: '/:locale/privacy',
    component: Layout,
    children: [
      {
        path: '',
        name: 'privacy',
        component: Privacy,
        meta: {
          requiresAuth: false,
          breadcrumbs: [
            { routeName: 'index', localeLabel: 'BREADCRUMBS.INDEX', redirectPath: '' },
            { routeName: '', localeLabel: 'PRIVACY.TITLE', redirectPath: '' }
          ],
          metaInfo: {
            title: 'PRIVACY.TITLE',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  },
  {
    path: '/:locale/about',
    component: Layout,
    children: [
      {
        path: '',
        name: 'about',
        component: About,
        meta: {
          requiresAuth: false,
          breadcrumbs: [
            { routeName: 'index', localeLabel: 'BREADCRUMBS.INDEX', redirectPath: '' },
            { routeName: '', localeLabel: 'ABOUT.TITLE', redirectPath: '' }
          ],
          metaInfo: {
            title: 'ABOUT.TITLE',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  },
  {
    path: '/:locale/cooperation',
    component: Layout,
    children: [
      {
        path: '',
        name: 'cooperation',
        component: Cooperation,
        meta: {
          requiresAuth: false,
          breadcrumbs: [
            { routeName: 'index', localeLabel: 'BREADCRUMBS.INDEX', redirectPath: '' },
            { routeName: '', localeLabel: 'COOPERATION.TITLE', redirectPath: '' }
          ],
          metaInfo: {
            title: 'COOPERATION.TITLE',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  },
  {
    path: '/:locale/mediaKit',
    component: Layout,
    children: [
      {
        path: '',
        name: 'mediaKit',
        component: MediaKit,
        meta: {
          requiresAuth: false,
          breadcrumbs: [
            { routeName: 'index', localeLabel: 'BREADCRUMBS.INDEX', redirectPath: '' },
            { routeName: '', localeLabel: 'MEDIA_KIT.TITLE', redirectPath: '' }
          ],
          metaInfo: {
            title: 'MEDIA_KIT.TITLE',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  },
  // {
  //   path: '/:locale/membership',
  //   component: Layout,
  //   children: [
  //     {
  //       path: '',
  //       name: 'membership',
  //       component: Membership,
  //       meta: {
  //         requiresAuth: false,
  //         metaInfo: {
  //           title: 'MEMBERSHIP.TITLE',
  //           meta: [
  //             { vmid: 'image', name: 'image', content: indexMetaImage },
  //             { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
  //           ]
  //         }
  //       }
  //     }
  //   ]
  // },
  {
    path: '/:locale/member',
    component: Layout,
    children: [
      {
        path: '',
        redirect: '/'
      },
      {
        path: 'notification/:id',
        name: 'notification',
        component: Notification,
        meta: {
          requiresAuth: true,
          breadcrumbs: [
            { routeName: 'index', localeLabel: 'BREADCRUMBS.INDEX', redirectPath: '' },
            { routeName: '', localeLabel: 'MEMBER.NOTIFICATION.TITLE', redirectPath: '' }
          ],
          metaInfo: {
            title: 'MEMBER.NOTIFICATION.TITLE',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      },
      {
        path: ':id',
        name: 'memberPage',
        component: Member,
        meta: {
          // requiresAuth: true,
          breadcrumbs: [
            { routeName: 'index', localeLabel: 'BREADCRUMBS.INDEX', redirectPath: '' },
            { routeName: '', localeLabel: 'MEMBER.INDEX.TITLE', redirectPath: '' }
          ],
          metaInfo: {
            title: 'MEMBER.INDEX.TITLE',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  },
  {
    path: '/:locale/game',
    component: Layout,
    children: [
      {
        path: '',
        name: 'game',
        component: Game,
        meta: {
          requiresAuth: false,
          metaInfo: {
            title: 'GAME.TITLE',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  },
  {
    path: '/:locale/game/piano',
    component: GameLayout,
    children: [
      {
        path: '',
        name: 'piano',
        component: Piano,
        meta: {
          requiresAuth: false,
          metaInfo: {
            title: 'GAME.TITLE',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  },
  {
    path: '/:locale/transaction',
    component: Layout,
    children: [
      {
        path: '',
        name: 'transaction',
        component: Transaction,
        meta: {
          requiresAuth: false,
          metaInfo: {
            title: 'TRANSACTION.TITLE',
            meta: [
              { vmid: 'image', name: 'image', content: indexMetaImage },
              { vmid: 'og:image', name: 'og:image', content: indexMetaImage }
            ]
          }
        }
      }
    ]
  }
]

const getRoutes = productionName => {
  let routes = []
  if (productionName.toUpperCase() === 'READ') {
    routes = routes.concat(baseRotes).concat(readRoutes)
    // routes = routes.concat(listenRoutes)
  } else if (productionName.toUpperCase() === 'LISTEN') {
    routes = routes.concat(baseRotes).concat(listenRoutes)
    // routes = routes.concat(readRoutes)
  } else if (productionName.toUpperCase() === 'CONCERT') {
    routes = routes.concat(baseRotes).concat(concertRoutes)
  } else if (productionName.toUpperCase() === 'CSS') {
    routes = routes.concat(baseRotes).concat(cssRoutes)
  } else if (productionName.toUpperCase() === 'AIR') {
    routes = routes.concat(baseRotes).concat(airRoutes)
  }
  // allow all production in sub path
  // routes = routes.concat(listenRoutes).concat(concertRoutes).concat(cssRoutes)
  return routes
}

const routes = getRoutes(currentProduction)

const router = new Router({
  scrollBehavior(to, from, savedPosition) {
    const isSamePage = (to && to.name) === (from && from.name)
    const isSameLocale = (to && to.params && to.params.locale) === (from && from.params && from.params.locale)
    if (isSamePage && isSameLocale) {
      return ''
    } else {
      return { x: 0, y: 0 }
    }
  },
  mode: 'history',
  routes
})

router.beforeEach(async (to, from, next) => {
  if (!from.params.locale && to.params.locale) {
    await loadLanguageAsync(to.params.locale)
  }
  if (!from.params.locale || (from.params.locale !== to.params.locale)) {
    // Check entry user locale is matched with default locale setting
    const localePathRegExp = new RegExp(/^\/(tw|cn|us|jp|zh|en|ja){1}/gi)
    if (to.path.match(localePathRegExp)) {
      await loadLanguageAsync(to.params.locale)
    } else {
      // console.error('Invalid locale params in path.')
    }
  }
  // ///////////////////////////////////////////////////////////////////////////////////////
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!store.getters.isAuthenticated) {
      const token = getLocalStorage('token')
      if (token) {
        const user = await store.dispatch('USER_REQUEST', token)
        if (!user) {
          next({ path: '/', query: {} })
        }
        // pass
      } else {
        next({ path: '/', query: {} })
      }
    }
  }
  next() // not require auth or pass auth
})

// set router.beforeEach here!
// export default new Router({
//   mode: 'history',
//   routes
// })

export default router
